/**
 * PurchaseFlow Component - Main Entry Point
 *
 * A standalone, reusable purchase flow component for handling payments.
 *
 * Exports:
 * - Components: PurchaseFlow, MobilePurchaseFlow, Wrappers
 * - Types: All TypeScript interfaces for integration
 * - Constants: Error types and message keys
 * - Mobile utilities: Direct message posting functions
 *
 * @example
 * ```tsx
 * import { PurchaseFlow } from './purchase_flow';
 * import type { PurchaseFlowProps, AnalyticsConfig } from './purchase_flow';
 *
 * // Regular purchase (creditsRequested defaults to price)
 * <PurchaseFlow price={25} onComplete={() => {}} onCancel={() => {}} />
 *
 * // Discount offer: 15 credits for $14.99
 * <PurchaseFlow price={14.99} creditsRequested={15} onComplete={() => {}} onCancel={() => {}} />
 * ```
 */

// ============================================================================
// Main Components
// ============================================================================

export { default as PurchaseFlow } from './PurchaseFlow';
export { MobilePurchaseFlow } from './mobile';

// ============================================================================
// Container Components (Wrappers)
// ============================================================================

export {
  PurchaseFlowWrapper,
  PurchaseButtonWrapper
} from './containers';

// ============================================================================
// Types - Core
// ============================================================================

export type {
  // Main component props
  PurchaseFlowProps,
  MobileAppProps,

  // Payment types
  PaymentMethodKind,
  PaymentMethod,
  PaymentOption,
  PaymentMethodDisplay,

  // Purchase types
  PurchaseType,
  PurchaseDetails,
  Fees,

  // Flow types
  Screen,
  CloseReason,
  FlowMode
} from './types';

// ============================================================================
// Types - Wrappers
// ============================================================================

export type {
  PurchaseFlowWrapperProps,
  PurchaseButtonWrapperProps
} from './types';

// ============================================================================
// Types - Analytics
// ============================================================================

export type {
  AnalyticsConfig,
  AnalyticsEventName,
  EventProperties,
  OnAnalyticsEvent
} from './types';

// ============================================================================
// Types - Config
// ============================================================================

export type {
  PurchaseFlowConfig,
  PurchaseFlowTheme,
  WithCaptchaFn,
  OnLogEvent,
  LogLevel
} from './types';

// ============================================================================
// Constants
// ============================================================================

export {
  PAYMENT_ERROR_TYPES,
  SCREENS,
  PURCHASE_FLOW_ICONS,
  PURCHASE_FLOW_TEXT_KEYS
} from './utils/constants';

export { getSuccessButtonTextKey } from './utils/successButtonKey';
export type { SuccessButtonKeyParams } from './utils/successButtonKey';

// ============================================================================
// Mobile Messaging Utilities
// ============================================================================

export {
  postMessageToApp,
  sendInitialLoaded,
  sendPayCompleted,
  sendDismiss,
  sendNavigationUpdate,
  sendAnalytics,
  messageToAppKeys
} from './mobile';
